/**
 * 飞行进程时间轴组件
 * 以甘特图形式展示每架飞机未来3天（今天、明天、后天）的航班任务
 * 支持维护事件显示
 */
import React, { memo, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
    findFlightTime,
    getEffectiveArrivalTime,
    getEffectiveDepartureTime,
} from "./flightTimelineUtils";
import "./FlightTimeline.css";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * @param {Object} props
 * @param {Array} props.data - 航班数据 [{aircraft_registration, flights: [...]}]
 * @param {Object} props.maintenanceEvents - 维护事件数据 {aircraft_registration: [{...}]}
 * @param {string} props.currentTime - 当前时间
 */
const FlightTimeline = ({ data = [], maintenanceEvents = {}, currentTime = null }) => {
    // 计算时间窗口（北京时间今天00:00 - 后天23:59）
    const timeWindow = useMemo(() => {
        const now = currentTime ? dayjs(currentTime) : dayjs();
        const bjTime = now.tz("Asia/Shanghai");

        // 今天00:00
        const startTime = bjTime.startOf("day");
        // 后天23:59
        const endTime = bjTime.add(2, "day").endOf("day");

        return {
            start: startTime.utc(), // 转回UTC
            end: endTime.utc(), // 转回UTC
            days: [
                startTime.format("MM-DD"),
                startTime.add(1, "day").format("MM-DD"),
                startTime.add(2, "day").format("MM-DD"),
            ],
        };
    }, [currentTime]);

    // 计算当前时间线位置（相对于时间窗口的百分比）
    const currentTimePosition = useMemo(() => {
        if (!currentTime) return 0;

        const now = dayjs(currentTime).tz("Asia/Shanghai");
        const totalMinutes = timeWindow.end.diff(timeWindow.start, "minute");
        const elapsedMinutes = now.diff(timeWindow.start, "minute");

        return Math.max(0, Math.min(100, (elapsedMinutes / totalMinutes) * 100));
    }, [currentTime, timeWindow]);

    // 计算日界分隔线位置（基于与航班条相同的 totalMinutes 基准，确保 std=00:00 的航班能严格对齐）
    // 返回 0~1 的小数，方便 CSS calc 计算（贯通 overlay 时需要考虑左侧机号列宽度）
    const dayDividerFractions = useMemo(() => {
        const totalMinutes = timeWindow.end.diff(timeWindow.start, "minute");
        const day1Offset = timeWindow.start.clone().add(1, "day").diff(timeWindow.start, "minute");
        const day2Offset = timeWindow.start.clone().add(2, "day").diff(timeWindow.start, "minute");
        return [day1Offset / totalMinutes, day2Offset / totalMinutes];
    }, [timeWindow]);

    // 计算时间条的位置和宽度（通用方法）
    // 返回 leftNum/widthNum (原始浮点) + left/width (CSS字符串)
    const calculateBarPosition = (startTimeStr, endTimeStr) => {
        const stdTime = dayjs(startTimeStr).utc();
        const staTime = dayjs(endTimeStr).utc();

        const totalMinutes = timeWindow.end.diff(timeWindow.start, "minute");
        
        const startOffset = stdTime.diff(timeWindow.start, "minute");
        const originalDuration = staTime.diff(stdTime, "minute");
        
        let effectiveDuration = originalDuration;
        if (startOffset < 0) {
            effectiveDuration += startOffset;
        }
        
        const endOffset = staTime.diff(timeWindow.end, "minute");
        if (endOffset > 0) {
            effectiveDuration -= endOffset;
        }

        const leftMinutes = Math.max(0, startOffset);
        const leftNum = (leftMinutes / totalMinutes) * 100;
        const widthNum = Math.max(0, (Math.max(0, effectiveDuration) / totalMinutes) * 100);

        return { left: `${leftNum}%`, width: `${widthNum}%`, leftNum, widthNum };
    };

    // 根据航班状态返回CSS类名
    const getFlightStatusClass = (status, isFerry = false) => {
        if (isFerry) {
            if (status === "DEPARTED" || status === "ACTIVE") return "status-ferry-active";
            return "status-ferry";
        }
        
        const classMap = {
            SCHEDULED: "status-scheduled",
            ACTIVE: "status-active",
            DEPARTED: "status-departed",
            ARRIVED: "status-arrived",
            COMPLETED: "status-arrived",
            DELAYED: "status-delayed",
            CANCELLED: "status-cancelled",
        };
        return classMap[status] || "status-scheduled";
    };

    const renderMaintenanceEvents = (aircraftReg, aircraftFlights) => {
        const events = maintenanceEvents[aircraftReg];
        if (!events || events.length === 0) return null;

        return events.map((event) => {
            const isPending = event.planning_status === "PENDING";
            let startTime, endTime, tooltip;

            if (isPending) {
                const expectedDate = event.expected_start_date;
                if (!expectedDate) return null;
                startTime = dayjs.tz(expectedDate, "Asia/Shanghai").startOf("day").utc().format();
                endTime = dayjs.tz(expectedDate, "Asia/Shanghai").endOf("day").utc().format();
                const title = event.title || "定检维护";
                tooltip = `${title}（计划待定）\n预计: ${expectedDate}`;
            } else {
                startTime = findFlightTime(aircraftFlights, event.prev_flight_number, event.prev_flight_date, "sta");
                endTime = findFlightTime(aircraftFlights, event.next_flight_number, event.next_flight_date, "std");

                if (!startTime) {
                    if (!event.prev_flight_date) {
                        startTime = timeWindow.start.format();
                    } else {
                        const fallback = dayjs.tz(event.prev_flight_date, "Asia/Shanghai").endOf("day").utc();
                        startTime = fallback.isBefore(timeWindow.start) ? timeWindow.start.format() : fallback.format();
                    }
                }
                if (!endTime) {
                    if (!event.next_flight_date) {
                        endTime = timeWindow.end.format();
                    } else {
                        const fallback = dayjs.tz(event.next_flight_date, "Asia/Shanghai").startOf("day").utc();
                        endTime = fallback.isAfter(timeWindow.end) ? timeWindow.end.format() : fallback.format();
                    }
                }
                const title = event.title || "定检维护";
                tooltip = `${title}\n${event.prev_flight_number} 落地后 → ${event.next_flight_number} 起飞前`;
            }

            const position = calculateBarPosition(startTime, endTime);
            if (position.widthNum <= 0) return null;

            const title = event.title || "定检维护";

            return (
                <div
                    key={`maint-${event.id}`}
                    className="flight-bar"
                    style={{
                        left: position.left,
                        width: position.width,
                    }}
                    title={tooltip}
                >
                    <div className="flight-bar-main status-maintenance">
                        <span className="flight-label">{title}</span>
                    </div>
                </div>
            );
        });
    };

    if (!data || data.length === 0) {
        return (
            <div className="flight-timeline-empty">
                <p>暂无航班数据</p>
            </div>
        );
    }

    return (
        <div className="flight-timeline-container">
            {/* 顶部日期标题 */}
            <div className="timeline-header">
                <div className="timeline-aircraft-header">机号</div>
                <div className="timeline-dates-header">
                    {timeWindow.days.map((date) => (
                        <div key={date} className="timeline-date">
                            {date}
                        </div>
                    ))}
                </div>
            </div>

            {/* 时间轴内容 */}
            <div className="timeline-content">
                {data.map((aircraft) => (
                    <div key={aircraft.aircraft_registration} className="timeline-row">
                        {/* 左侧飞机号 */}
                        <div className="timeline-aircraft-label">{aircraft.aircraft_registration}</div>

                        {/* 右侧时间轴 */}
                        <div className="timeline-track">
                            {/* 日列背景：把 track 切成 3 个并列日格子，相邻格子留 4px 间隙形成天然分隔 */}
                            {dayDividerFractions.length === 2 && (
                                <>
                                    <div
                                        className="timeline-day-column"
                                        style={{ left: 0, right: `calc(${(1 - dayDividerFractions[0]) * 100}% + 2px)` }}
                                    />
                                    <div
                                        className="timeline-day-column"
                                        style={{
                                            left: `calc(${dayDividerFractions[0] * 100}% + 2px)`,
                                            right: `calc(${(1 - dayDividerFractions[1]) * 100}% + 2px)`,
                                        }}
                                    />
                                    <div
                                        className="timeline-day-column"
                                        style={{ left: `calc(${dayDividerFractions[1] * 100}% + 2px)`, right: 0 }}
                                    />
                                </>
                            )}

                            {/* 航班时间条 */}
                            {(() => {
                                // 排序航班并预处理相邻航段共享机场
                                const sortedFlights = [...(aircraft.flights || [])].sort((a, b) =>
                                    dayjs(getEffectiveDepartureTime(a)).diff(dayjs(getEffectiveDepartureTime(b)))
                                );

                                const totalMinutes = timeWindow.end.diff(timeWindow.start, "minute");
                                const hideArr = new Set(); // 隐藏落地机场的索引
                                const hideDep = new Set(); // 隐藏起飞机场的索引
                                const sharedLabels = [];   // 共享中转标签

                                for (let i = 0; i < sortedFlights.length - 1; i++) {
                                    const curr = sortedFlights[i];
                                    const next = sortedFlights[i + 1];
                                    const currArr = curr.arrival_airport || curr.route?.split("-")[1] || "";
                                    const nextDep = next.departure_airport || next.route?.split("-")[0] || "";

                                    if (currArr && currArr === nextDep) {
                                        const currEnd = dayjs(getEffectiveArrivalTime(curr)).utc();
                                        const nextStart = dayjs(getEffectiveDepartureTime(next)).utc();
                                        const gapMinutes = nextStart.diff(currEnd, "minute");
                                        const gapPercent = (gapMinutes / totalMinutes) * 100;

                                        if (gapPercent < 4) {
                                            hideArr.add(i);
                                            hideDep.add(i + 1);
                                            // 共享标签定位在两段之间居中
                                            const midMinutes = currEnd.diff(timeWindow.start, "minute") + gapMinutes / 2;
                                            const midPercent = (midMinutes / totalMinutes) * 100;
                                            sharedLabels.push({
                                                airport: currArr,
                                                position: `${midPercent}%`,
                                                key: `shared-${i}`
                                            });
                                        }
                                    }
                                }

                                return (
                                    <>
                                        {sortedFlights.map((flight, index) => {
                                            const effectiveDepartureTime = getEffectiveDepartureTime(flight);
                                            const effectiveArrivalTime = getEffectiveArrivalTime(flight);
                                            const position = calculateBarPosition(
                                                effectiveDepartureTime,
                                                effectiveArrivalTime
                                            );

                                            const isFerry = flight.flight_type === "FERRY" ||
                                                flight.is_ferry ||
                                                flight.service_type === "FERRY" ||
                                                flight.service_type === "F";
                                            const statusClass = getFlightStatusClass(flight.status, isFerry);

                                            // 实际 > 预计 > 计划，与航班条定位使用相同时间口径
                                            const departureTimeBJ = dayjs(effectiveDepartureTime).tz("Asia/Shanghai").format("HH:mm");
                                            const arrivalTimeBJ = dayjs(effectiveArrivalTime).tz("Asia/Shanghai").format("HH:mm");

                                            const depAirport = flight.departure_airport || flight.route?.split("-")[0] || "";
                                            const arrAirport = flight.arrival_airport || flight.route?.split("-")[1] || "";

                                            let flightProgress = 0;
                                            const isInProgress = (flight.status === "DEPARTED" || flight.status === "ACTIVE");
                                            if (isInProgress && currentTime) {
                                                if (position.widthNum > 0) {
                                                    flightProgress = Math.max(0, Math.min(100, (currentTimePosition - position.leftNum) / position.widthNum * 100));
                                                }
                                            }

                                            return (
                                                <div
                                                    key={flight.id ?? `${flight.flight_number}-${flight.std}`}
                                                    className="flight-bar"
                                                    style={{
                                                        left: position.left,
                                                        width: position.width,
                                                    }}
                                                    title={`${flight.flight_number}\n${departureTimeBJ}-${arrivalTimeBJ}\n${depAirport}→${arrAirport}`}
                                                >
                                                    <div className="flight-time">
                                                        <span>{departureTimeBJ}</span>
                                                        <span>{arrivalTimeBJ}</span>
                                                    </div>
                                                    <div
                                                        className={`flight-bar-main ${statusClass} ${isInProgress ? 'has-progress' : ''}`}
                                                        style={isInProgress ? { '--progress': `${flightProgress}%` } : {}}
                                                    >
                                                        <span className="flight-label">{flight.flight_number}</span>
                                                    </div>
                                                    <div className="flight-route">
                                                        <span style={hideDep.has(index) ? { visibility: 'hidden' } : {}}>{depAirport}</span>
                                                        <span style={hideArr.has(index) ? { visibility: 'hidden' } : {}}>{arrAirport}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* 共享中转机场标签 */}
                                        {sharedLabels.map(label => (
                                            <div
                                                key={label.key}
                                                className="shared-airport-label"
                                                style={{ left: label.position }}
                                            >
                                                {label.airport}
                                            </div>
                                        ))}
                                    </>
                                );
                            })()}

                            {/* 维护事件条 */}
                            {renderMaintenanceEvents(aircraft.aircraft_registration, aircraft.flights)}

                            {/* 当前时间指示线 */}
                            {currentTime && (
                                <div className="current-time-line" style={{ left: `${currentTimePosition}%` }}>
                                    <div className="current-time-marker"></div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* 底部图例 */}
            <div className="timeline-legend">
                <div className="legend-item">
                    <div className="legend-color status-scheduled"></div>
                    <span className="legend-text">计划航班</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color status-ferry"></div>
                    <span className="legend-text">调机航段</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color status-maintenance"></div>
                    <span className="legend-text">维护事件</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color status-delayed"></div>
                    <span className="legend-text">延误航班</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color status-active"></div>
                    <span className="legend-text">执行中/已完成</span>
                </div>
            </div>
        </div>
    );
};

// 使用React.memo优化性能，避免不必要的重渲染
export default memo(FlightTimeline);
